import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(ScrollTrigger);
let mm = gsap.matchMedia();

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

export class HomeHeroAnimation {
  private element: HTMLElement;
  private title: HTMLHeadElement | null;
  private buttonSet: HTMLElement | null;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.title = this.element.querySelector(".home-hero__title");
      this.buttonSet = this.element.querySelector(".button-link-set");
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      this.animateHeadline();
      this.animateButtons();
    }
  }

  private animateHeadline() {
    document.fonts.ready.then(() => {
      gsap.set(this.title, { opacity: 1 });
      let split;

      SplitText.create(this.title, {
        type: "lines",
        mask: "lines",
        onSplit: (self) => {
          split = gsap.from(self.lines, {
            y: (index) => (index % 2 === 0 ? 20 : -20),
            autoAlpha: 0,
            stagger: 0.5,
            duration: 0.75,
            delay: 0.5,
            ease: "expo.out",
            onComplete: () => {
              self.revert();
            },
          });
          return split;
        },
      });
    });
  }

  private animateButtons() {
    gsap.to(this.buttonSet, {
      autoAlpha: 1,
      delay: 1.5,
      duration: 1.5,
      ease: "power2.out",
    });
  }
}

export class HomeButtonAnimation {
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      this.animateButton();
    }
  }

  private animateButton() {
    mm.add(
      {
        isMobile: "(max-width: 767px)",
        isDesktop: "(min-width: 768px)",
      },
      (context) => {
        const { isMobile = false, isDesktop = false } =
          context.conditions || {};
        gsap.from(this.element, {
          scrollTrigger: {
            trigger: this.element,
            markers: false,
            start: "top bottom",
            end: "buttom center",
            scrub: true,
          },
          opacity: isMobile ? 1 : 0,
          yoyo: false,
        });
      },
    );
  }
}

export class HomeImageAnimation {
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      this.animateImage();
    }
  }

  private animateImage() {
    mm.add(
      {
        isMobile: "(max-width: 767px)",
        isDesktop: "(min-width: 768px)",
      },
      (context) => {
        const { isMobile = false, isDesktop = false } =
          context.conditions || {};
        gsap.from(this.element, {
          scrollTrigger: {
            trigger: this.element,
            markers: false,
            start: "top bottom",
            end: "bottom center",
            scrub: true,
          },
          scale: isMobile ? 1 : 0.8,
          yoyo: false,
        });
      },
    );
  }
}

export class HomeSectionTitleAnimation {
  private element: HTMLElement;
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      this.animateSectionTitle();
    }
  }

  private animateSectionTitle() {
    mm.add(
      {
        isMobile: "(max-width: 767px)",
        isDesktop: "(min-width: 768px)",
      },
      (context) => {
        const { isMobile = false, isDesktop = false } =
          context.conditions || {};
        gsap.from(this.element, {
          scrollTrigger: {
            trigger: this.element,
            markers: false,
            start: "top bottom",
            end: "bottom center",
            scrub: true,
          },
          opacity: isMobile ? 1 : 0,
          y: isMobile ? 0 : 65,
          yoyo: false,
        });
      },
    );
  }
}

export default function homeAnimationsInit() {
  const homeHero = document.querySelector(".home-hero") as HTMLElement;
  new HomeHeroAnimation(homeHero);

  const homeButtons = document.querySelectorAll(
    ".hp-storytelling .button-link-set",
  ) as NodeListOf<HTMLImageElement>;

  for (let i = 0; i < homeButtons.length; i++) {
    new HomeButtonAnimation(homeButtons[i]);
  }

  const homeImages = document.querySelectorAll(
    ".home-hero__content-feature__wrapper .content-feature__media img, .hp-storytelling__media img, .home-explorer__media img",
  ) as NodeListOf<HTMLImageElement>;

  for (let i = 0; i < homeImages.length; i++) {
    new HomeImageAnimation(homeImages[i]);
  }

  const homeSectionTitles = document.querySelectorAll(
    ".hp-storytelling__text-content h2, .home-explorer__title, .home-quote__item, .home-video__section-title, .home-card-carousel__title, body:has(.home-hero) .newsletter-signup__title",
  ) as NodeListOf<HTMLElement>;

  for (let i = 0; i < homeSectionTitles.length; i++) {
    new HomeSectionTitleAnimation(homeSectionTitles[i]);
  }
}
